/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useState } from 'react';
import { Form } from 'antd';
import classnames from 'classnames';
import { useForm } from 'antd/es/form/Form';
import { observer } from 'mobx-react-lite';
import useHistory from '../../hooks/useHistory';
import { CreditScoreRangeType, CreditScoreRangeTypeV2, Prequal } from '../../schema';
import { usePrequal } from '../../stores/prequalStore';
import { formatCurrency, pushToDataLayer } from '../../utils';
import { withExpVariant } from '../../utils/kamExpVariant';
import PreQualFunnelLayout from '../PreQualFunnelLayout';
import { NavigationRow } from '../components/NavigationRow/NavigationRow';
import ResultsTooltip, {
  prequalApprovalRateToolTip,
  prequalApprovalTermToolTip,
  creditScoreToolTip,
  loanAmountToolTip,
} from '../components/ResultsTooltip/ResultsTooltip';
import SectionHeader, { HeaderProps } from '../components/SectionHeader/SectionHeader';
import styles from './ResultsStep.module.scss';
import PreQualDisclaimer from '../components/Disclaimer/Disclaimer';
import { DEFAULT_PREQUAL_APR_DISCLAIMER, isBoatTrader, isDev } from '../../constants';

type SummaryType = {
  hasDisclaimer?: boolean;
  hideLastItemBorder?: boolean;
  name: String;
  show?: boolean;
  tooltip?: JSX.Element | null;
  value?: String | null;
};

const getYears = (months: number | null = 0) => (months ?? 0) / 12;
const getFullName = (
  firstName: string | undefined,
  lastName: string | undefined
): string | null => {
  let name = '';

  if (firstName) {
    name = firstName;
  }

  if (lastName) {
    name = firstName ? `${name} ${lastName}` : lastName;
  }

  return name || null;
};

export const getPrequalResults = ({
  creditScore,
  downPayment,
  loanAmount,
  prequal,
  prequalApprovalMonthlyPayment,
  prequalApprovalRate,
  prequalApprovalTerm,
  purchasePrice,
}: {
  creditScore: CreditScoreRangeType | CreditScoreRangeTypeV2;
  downPayment: number;
  loanAmount: number;
  prequal: Partial<Prequal>;
  prequalApprovalMonthlyPayment: Prequal['prequalApprovalMonthlyPayment'];
  prequalApprovalRate: Prequal['prequalApprovalRate'];
  prequalApprovalTerm: Prequal['prequalApprovalTerm'];
  purchasePrice: number;
}) => ({
  default: [
    {
      name: 'Estimated Monthly Payment',
      value: prequalApprovalMonthlyPayment ? formatCurrency(prequalApprovalMonthlyPayment) : '',
      show: true,
    },
    { name: 'Down Payment', value: formatCurrency(downPayment), show: true },
    {
      name: 'Loan Amount',
      value: formatCurrency(loanAmount),
      tooltip: <ResultsTooltip tooltip={loanAmountToolTip} />,
      show: true,
    },
    {
      name: 'Credit Score',
      value: `(${creditScore})`,
      tooltip: <ResultsTooltip tooltip={creditScoreToolTip} />,
      show: false,
    },
    {
      name: 'Term in Years',
      value: prequalApprovalTerm ? `${getYears(prequalApprovalTerm)} Years` : '',
      tooltip: <ResultsTooltip tooltip={prequalApprovalTermToolTip} />,
      show: true,
    },
    {
      name: 'Estimated APR',
      value: prequalApprovalRate ? `${prequalApprovalRate?.toString()}%` : '',
      tooltip: <ResultsTooltip tooltip={prequalApprovalRateToolTip} />,
      hasDisclaimer: true,
      show: true,
    },
    { name: 'Estimated Purchase Power', value: formatCurrency(purchasePrice), show: true },
  ],
  declined: [
    {
      name: 'Monthly Payment',
      value: prequalApprovalMonthlyPayment ? formatCurrency(prequalApprovalMonthlyPayment) : '',
      show: true,
    },
    { name: 'Down Payment', value: formatCurrency(downPayment), show: true },
    {
      name: 'Name',
      value: getFullName(prequal?.firstName, prequal?.lastName) ?? 'N/A',
      show: true,
    },
    {
      name: 'Email',
      value: prequal?.email ?? 'N/A',
      show: true,
    },
    {
      name: 'Phone',
      value: prequal?.phone ?? 'N/A',
      show: true,
    },
    {
      name: 'Address',
      value: `${prequal.address?.address1}, ${prequal.address?.city}, ${prequal.address?.state}, ${prequal.address?.zip}`,
      show: true,
      hideLastItemBorder: true,
    },
  ],
});

export const getPrequalHeader = ({
  approved,
  purchasePrice,
  firstName,
  goToApplyNowLink,
  creditScore,
}: {
  purchasePrice: number;
  firstName: string;
  approved: boolean;
  goToApplyNowLink: () => void;
  creditScore: CreditScoreRangeType | CreditScoreRangeTypeV2 | undefined;
}) => ({
  default: {
    title: 'Results',
    subtitle: approved
      ? creditScore
        ? `Great news ${firstName}, your purchasing power is up to ${formatCurrency(
            purchasePrice
          )}!`
        : `We couldn't verify your identity with our partners. To help you move forward, we've estimated your purchasing power at ${formatCurrency(
            purchasePrice
          )}`
      : 'We need a little more information to qualify your loan.',
    content: approved ? (
      <>
        Rate offers can fluctuate over time, so <span onClick={goToApplyNowLink}>Apply Now</span> to
        lock-in your final rate today.
      </>
    ) : (
      <>Please continue with the loan application.</>
    ),
  },
  declined: {
    title: 'Results',
    subtitle:
      "We're Sorry! We currently don't have a loan program that matches our criteria for your request.",
    content: <></>,
  },
});

const ResultsStep = observer(() => {
  const prequalStore = usePrequal();
  const history = useHistory();
  const {
    prequal,
    boatInfo,
    isBDP,
    source,
    subsource,
    leadPage,
    utm = null,
    marketing = null,
  } = prequalStore;
  const [form] = useForm();
  const [summary, setSummary] = useState<SummaryType[]>([]);
  const [preApproved, setPreApproved] = useState(false);
  const [header, setHeader] = useState<HeaderProps>({ title: '', subtitle: '', content: '' });
  const [applyLink, setApplyLink] = useState('');
  const approved = prequal?.prequalApprovalStatus === 'Approved';
  const shouldShowDeclinedView = !approved;

  const goToApplyNowLink = () => {
    prequalStore.setResults();
    pushToDataLayer('link_click', {
      action_type: 'click',
      action_label: 'Apply now',
    });
    window.open(applyLink, '_blank');
  };

  useEffect(() => {
    pushToDataLayer(
      'pageview',
      withExpVariant({
        detailed_page_name: 'pre-qualify results',
        site_section: 'loan application',
        funnel: 'internal pre-qual',
      })
    );
  }, []);

  useEffect(() => {
    pushToDataLayer(
      'application_submitted',
      withExpVariant({
        'pre-qual': approved,
        application_id: prequal.leadUUID,
        funnel: 'internal pre-qual',
      })
    );
  }, [approved]);

  useEffect(() => {
    const {
      purchasePrice,
      downPayment,
      loanAmount,
      creditScore,
      prequalApprovalRate,
      prequalApprovalTerm,
      prequalApprovalMonthlyPayment,
      leadUUID,
      firstName,
      keyCode,
      promoCode,
      leadOrigin,
      repCode,
      imtId,
    } = prequal as Prequal;

    let boatLink =
      `${isBoatTrader() || isDev() ? '/boat-loans' : ''}/apply` +
      `/?purpose=Boat&prequal=true&prequalImtId=${imtId}` +
      `${leadOrigin ? `&leadOrigin=${leadOrigin}` : ''}` +
      `${source ? `&source=${source}` : ''}` +
      `${subsource ? `&subsource=${subsource}` : ''}` +
      `${leadPage ? `&leadPage=${leadPage}` : ''}` +
      `${keyCode ? `&keycode=${keyCode}` : ''}` +
      `${promoCode ? `&promocode=${promoCode}` : ''}` +
      `${repCode ? `&repid=${repCode}` : ''}` +
      `${utm?.utmCampaign ? `&utm_campaign=${utm?.utmCampaign}` : ''}` +
      '&utm_content=pre-qual-funnel-to-app' +
      `${utm?.utmSource ? `&utm_source=${utm?.utmSource}` : ''}` +
      `${utm?.utmMedium ? `&utm_medium=${utm?.utmMedium}` : ''}` +
      `${utm?.utmTerm ? `&utm_term=${utm?.utmTerm}` : ''}` +
      `${utm?.utmId ? `&utm_id=${utm?.utmId}` : ''}` +
      `${utm?.utmName ? `&utm_name=${utm?.utmName}` : ''}` +
      `${marketing?.adGroup ? `&adgroup=${marketing?.adGroup}` : ''}` +
      `${marketing?.adGroupId ? `&adgroupid=${marketing?.adGroupId}` : ''}` +
      `${marketing?.campaignName ? `&campaign=${marketing?.campaignName}` : ''}` +
      `${marketing?.campaignId ? `&campaignid=${marketing?.campaignId}` : ''}`;

    if (isBDP) {
      boatLink += `${boatInfo ? `&boatInfo=${boatInfo}` : ''}`;
    }
    boatLink += `&leadUUID=${leadUUID}`;
    setApplyLink(boatLink);

    const display = shouldShowDeclinedView ? 'declined' : 'default';

    const results = getPrequalResults({
      creditScore,
      downPayment,
      loanAmount,
      prequalApprovalMonthlyPayment,
      prequalApprovalRate,
      prequalApprovalTerm,
      purchasePrice,
      prequal,
    })?.[display];

    const resultsHeader = getPrequalHeader({
      approved,
      firstName,
      goToApplyNowLink,
      purchasePrice,
      creditScore,
    })?.[display];

    setSummary(results);
    setPreApproved(approved);
    setHeader(resultsHeader);
  }, [prequal, form, isBDP, source, boatInfo, applyLink, shouldShowDeclinedView, approved]);

  return (
    <PreQualFunnelLayout>
      <SectionHeader header={header} />
      <Form form={form} initialValues={prequal} onFinish={goToApplyNowLink}>
        <div className={styles.resultsContainer}>
          {summary.map((item, index) => {
            const isLastItem = index === summary.length - 1;
            return item.show ? (
              <div
                key={`result-${item.name}}`}
                className={classnames(styles.resultItem, {
                  [styles.itemBorder]: isLastItem && !item?.hideLastItemBorder,
                })}
              >
                <div className={styles.resultName}>
                  {item.name}
                  {item.hasDisclaimer ? <span>*</span> : null}
                  {item.tooltip}
                </div>
                <div className={styles.dashedLine} />
                <div
                  className={classnames(styles.resultValue, {
                    [styles.declinedContentValue]: shouldShowDeclinedView,
                  })}
                >
                  {item.value}
                </div>
              </div>
            ) : (
              <></>
            );
          })}
        </div>
      </Form>
      <NavigationRow isLastStep canContinue form={form} />
      <div className={styles.disclaimerContainer}>
        <PreQualDisclaimer text="Not a commitment to lend or a firm offer of credit. Invitation to apply, subject to full application and credit approval." />
      </div>
      {!shouldShowDeclinedView && (
        <PreQualDisclaimer text={prequal?.aprDisclaimer ?? DEFAULT_PREQUAL_APR_DISCLAIMER} />
      )}
    </PreQualFunnelLayout>
  );
});

export default ResultsStep;
